import {
    StatusBadgeComponent,
    TableActionsComponent,
    TableActionsConfig,
    DateDisplayComponent,
    ReferenceNumberEditComponent,
    BundleNameEditComponent,
    TextDisplayComponent,
    TranslatedTextDisplayComponent,
    DiscrepanciesDisplayComponent,
    PalletTicketsDisplayComponent,
    LicensePlatesDisplayComponent,
    LicensePlateType,
    ConsignorNameWithExchangeComponent,
    CopyableTextDisplayComponent,
} from '@app/shared/components/table-cell-components';
import {
    TableRowData,
    hasStatus,
    hasReadOnly,
    hasOrgSiteKey,
} from '@app/shared/models/table-data-types';
import { Type } from '@angular/core';
import {
    SelfCheckinRegistrationCompletedEventPayload,
    SelfCheckinDriverWaitingEventPayload,
    SelfCheckinCheckinFinishedEventPayload,
    DeliveryNoteBundleEvents,
    EventPayload,
    DeliveryProperties,
} from 'api/models';

// column value types
export type ColumnValue = string | number | boolean | Date | null | undefined;
type ComponentInputs = {
    value?: ColumnValue;
    row?: TableRowData;
    parentRow?: TableRowData;
    configuration?: TableActionsConfig;
    item?: TableRowData;
    organizationSiteKey?: string;
    orgType?: 'consignor' | 'carrier' | 'consignee';
    readOnly?: boolean;
    format?: string;
    bundleStatus?: string;
    plateType?: LicensePlateType;
};
type ViewContext = Record<string, ColumnValue>;

// Type guard to check if row has properties field
type TableRowWithProperties = TableRowData & {
    properties?: DeliveryProperties;
};

function hasProperties(row: TableRowData): row is TableRowWithProperties {
    return (
        row &&
        typeof row === 'object' &&
        'properties' in row &&
        row.properties !== null &&
        row.properties !== undefined
    );
}

// Type guard to check if row has event payload
function hasEventPayload(row: TableRowData): row is TableRowData & {
    payload: EventPayload;
} {
    return (
        row &&
        typeof row === 'object' &&
        'payload' in row &&
        row.payload &&
        typeof row.payload === 'object'
    );
}

// Type guard to check if row has event payload with deliveryNoteBundles
function hasDeliveryNoteBundlesPayload(
    row: TableRowData
): row is TableRowData & {
    payload:
        | SelfCheckinRegistrationCompletedEventPayload
        | SelfCheckinDriverWaitingEventPayload
        | SelfCheckinCheckinFinishedEventPayload;
} {
    if (!hasEventPayload(row)) {
        return false;
    }
    const payload = row.payload as
        | SelfCheckinRegistrationCompletedEventPayload
        | SelfCheckinDriverWaitingEventPayload
        | SelfCheckinCheckinFinishedEventPayload;
    return (
        'deliveryNoteBundles' in payload &&
        Array.isArray(payload.deliveryNoteBundles)
    );
}

// Type guard to check if row has self-checkin event payload with driver info
function hasSelfCheckinDriverPayload(row: TableRowData): row is TableRowData & {
    payload:
        | SelfCheckinRegistrationCompletedEventPayload
        | SelfCheckinDriverWaitingEventPayload;
} {
    if (!hasEventPayload(row)) {
        return false;
    }
    const payload = row.payload as any;
    return (
        (payload.eventType === 'self_checkin_registration_completed' ||
            payload.eventType === 'self_checkin_driver_waiting') &&
        'driverName' in payload &&
        'driverNumberPlate' in payload
    );
}

// Type guard to check if row has self-checkin event payload with selfCheckinData
function hasSelfCheckinDataPayload(row: TableRowData): row is TableRowData & {
    payload:
        | SelfCheckinRegistrationCompletedEventPayload
        | SelfCheckinDriverWaitingEventPayload;
} {
    if (!hasEventPayload(row)) {
        return false;
    }
    const payload = row.payload as any;
    return (
        (payload.eventType === 'self_checkin_registration_completed' ||
            payload.eventType === 'self_checkin_driver_waiting') &&
        'selfCheckinData' in payload &&
        payload.selfCheckinData &&
        typeof payload.selfCheckinData === 'object'
    );
}

export interface ColumnConfig {
    key: string;
    header: string;
    sortable?: boolean;
    filterable?: boolean;
    width?: string;
    component?: Type<unknown>;
    componentInputs?: (
        row: TableRowData,
        value: ColumnValue,
        parentRow?: TableRowData
    ) => ComponentInputs;
    visible?: boolean | ((context: ViewContext) => boolean);
    selectableCondition?: (item: TableRowData) => boolean;
}

export class ColumnCreator {
    // Selection column
    static select(options?: {
        width?: string;
        visible?: boolean | ((context: ViewContext) => boolean);
        selectableCondition?: (item: TableRowData) => boolean;
    }): ColumnConfig {
        return {
            key: 'select',
            header: '',
            sortable: false,
            filterable: false,
            width: options?.width || '60px',
            visible: options?.visible,
            selectableCondition: options?.selectableCondition,
        };
    }

    // Details/Expandable column
    static details(options?: { width?: string }): ColumnConfig {
        return {
            key: 'details',
            header: '',
            sortable: false,
            filterable: false,
            width: options?.width || '60px',
        };
    }

    // Actions column
    static actions(config: {
        header?: string;
        width?: string;
        configuration:
            | TableActionsConfig
            | ((
                  row: TableRowData,
                  value: ColumnValue,
                  parentRow?: TableRowData
              ) => TableActionsConfig);
    }): ColumnConfig {
        return {
            key: 'actions',
            header:
                config.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.ACTIONS',
            sortable: false,
            filterable: false,
            width: config.width || '200px',
            component: TableActionsComponent,
            componentInputs: (
                row: TableRowData,
                value: ColumnValue,
                parentRow?: TableRowData
            ) => ({
                item: row,
                configuration:
                    typeof config.configuration === 'function'
                        ? config.configuration(row, value, parentRow)
                        : config.configuration,
            }),
        };
    }

    // Bundle Number column
    static bundleNumber(options?: {
        header?: string;
        sortable?: boolean;
        filterable?: boolean;
    }): ColumnConfig {
        return {
            key: 'bundleNumber',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
            sortable: options?.sortable !== false,
            filterable: options?.filterable !== false,
        };
    }

    // Bundle Name column
    static bundleName(options?: {
        header?: string;
        editable?: boolean;
        editableCondition?: (row: TableRowData) => boolean;
        orgType?: 'consignor' | 'carrier' | 'consignee';
    }): ColumnConfig {
        const config: ColumnConfig = {
            key: 'name',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.BUNDLE-NAME',
            sortable: true,
            filterable: true,
        };

        if (options?.editable) {
            config.component = BundleNameEditComponent;
            config.componentInputs = (
                row: TableRowData,
                value: ColumnValue
            ) => ({
                value,
                row,
                organizationSiteKey: hasOrgSiteKey(row) ? row.orgSiteKey : '',
                orgType: options.orgType || 'consignor',
                readOnly: options.editableCondition
                    ? !options.editableCondition(row)
                    : (hasStatus(row) && row.status !== 'open') ||
                      (hasReadOnly(row) && row.read_only === true),
            });
        }

        return config;
    }

    // Carrier Name column
    static carrierName(options?: { header?: string }): ColumnConfig {
        return {
            key: 'carrierName',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.CARRIER-NAME',
            sortable: true,
            filterable: true,
        };
    }

    // Consignor Name column
    static consignorName(options?: { header?: string }): ColumnConfig {
        return {
            key: 'consignorName',
            header:
                options?.header ||
                'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
            sortable: true,
            filterable: true,
        };
    }

    // Consignor Name with Load Carrier Exchange column
    static consignorNameWithExchange(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'consignorName',
            header:
                options?.header ||
                'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNOR-NAME',
            sortable: true,
            filterable: true,
            component: ConsignorNameWithExchangeComponent,
            componentInputs: (
                row: TableRowData,
                value: ColumnValue,
                parentRow?: TableRowData
            ) => ({
                value,
                row,
                parentRow,
            }),
        };
    }

    // Consignee Name column
    static consigneeName(options?: { header?: string }): ColumnConfig {
        return {
            key: 'consigneeName',
            header:
                options?.header ||
                'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.CONSIGNEE-NAME',
            sortable: true,
            filterable: true,
        };
    }

    // Status column
    static status(options?: {
        header?: string;
        key?: string;
        sortable?: boolean;
        filterable?: boolean;
        showDiscrepanciesIcon?: boolean;
    }): ColumnConfig {
        return {
            key: options?.key || 'status',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.STATE',
            sortable: options?.sortable !== false,
            filterable: options?.filterable !== false,
            component: StatusBadgeComponent,
            componentInputs: (row: TableRowData, value: ColumnValue) => ({
                value,
                row,
                showDiscrepanciesIcon: options?.showDiscrepanciesIcon || false,
            }),
        };
    }

    // Date columns
    static date(options: {
        key: string;
        header: string;
        format?: string;
        showTime?: boolean;
        showTimeConditionally?: boolean;
        sortable?: boolean;
        filterable?: boolean;
    }): ColumnConfig {
        // Determine format: if showTime is true, use 'dd.MM.yyyy HH:mm', otherwise use custom format or default
        const dateFormat = options.showTime
            ? 'dd.MM.yyyy HH:mm'
            : options.format || 'dd.MM.yyyy';

        return {
            key: options.key,
            header: options.header,
            sortable: options.sortable !== false,
            filterable: options.filterable,
            component: DateDisplayComponent,
            componentInputs: (
                row: TableRowData,
                value: ColumnValue,
                parentRow?: TableRowData
            ) => ({
                value: value,
                row: row,
                format: dateFormat,
                showTimeConditionally: options.showTimeConditionally ?? false,
                status:
                    (row as any).status ||
                    (row as any).deliveryNoteBundleStatus ||
                    (parentRow as any)?.status,
            }),
        };
    }

    // Date Shipped column
    static dateShipped(options?: {
        header?: string;
        showTime?: boolean;
        filterable?: boolean;
    }): ColumnConfig {
        return this.date({
            key: 'checkoutDate',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.SHIPPING-DATE',
            showTime: options?.showTime,
            filterable: options?.filterable,
        });
    }

    // Date Received column
    static dateReceived(options?: {
        header?: string;
        showTime?: boolean;
    }): ColumnConfig {
        return this.date({
            key: 'checkinDate',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.RECEIVE-DATE',
            showTime: options?.showTime,
        });
    }

    // Date Announced column
    static dateAnnounced(options?: {
        header?: string;
        showTime?: boolean;
    }): ColumnConfig {
        return this.date({
            key: 'dateAnnounced',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.ADVISED-DATE',
            showTime: options?.showTime,
            showTimeConditionally: true, // Show time when row.showDateAnnouncedTime is true
        });
    }

    // Date Created column
    static dateCreated(options?: {
        header?: string;
        showTime?: boolean;
    }): ColumnConfig {
        return this.date({
            key: 'dateCreated',
            header:
                options?.header || 'INCOMING-GOODS.TABLE.NOTIFICATIONS.DATE',
            showTime: options?.showTime,
            filterable: true,
        });
    }

    // Date Checkout column
    static dateCheckout(options?: {
        header?: string;
        format?: string;
        showTime?: boolean;
    }): ColumnConfig {
        return this.date({
            key: 'checkoutDate',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.TOURS.HEADERS.SENT-DATE',
            format: options?.format || 'dd.MM.yyyy',
            showTime: options?.showTime,
            filterable: true,
        });
    }

    // Reference Number column
    static referenceNumber(options: {
        key?: string;
        header?: string;
        readOnly?:
            | boolean
            | ((row: TableRowData, parentRow?: TableRowData) => boolean);
        orgType: 'consignor' | 'carrier' | 'consignee';
        organizationSiteKeyResolver?: (
            row: TableRowData,
            parentRow?: TableRowData
        ) => string;
    }): ColumnConfig {
        return {
            key: options.key || 'properties.referenceNumber',
            header:
                options.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.REFERENCE-NUMBER',
            sortable: true,
            filterable: true,
            component: ReferenceNumberEditComponent,
            componentInputs: (
                row: TableRowData,
                _value: ColumnValue,
                parentRow?: TableRowData
            ) => {
                // Always extract directly from properties, ignore the framework-provided value
                const extractedValue = hasProperties(row)
                    ? row.properties?.referenceNumber || ''
                    : '';
                return {
                    value: extractedValue,
                    organizationSiteKey: options.organizationSiteKeyResolver
                        ? options.organizationSiteKeyResolver(row, parentRow)
                        : hasOrgSiteKey(row)
                          ? row.orgSiteKey
                          : parentRow && hasOrgSiteKey(parentRow)
                            ? parentRow.orgSiteKey
                            : '',
                    readOnly:
                        typeof options.readOnly === 'function'
                            ? options.readOnly(row, parentRow)
                            : options.readOnly,
                    orgType: options.orgType,
                };
            },
        };
    }

    // Delivery Note Number column
    static deliveryNoteNumber(options?: {
        header?: string;
        sortable?: boolean;
        filterable?: boolean;
    }): ColumnConfig {
        return {
            key: 'number',
            header:
                options?.header ||
                'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NAME',
            sortable: options?.sortable !== false,
            filterable: options?.filterable !== false,
            width: '160px',
        };
    }

    // Order Number column
    static orderNumber(options?: { header?: string }): ColumnConfig {
        return {
            key: 'orderNumber',
            header:
                options?.header ||
                'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO',
            sortable: true,
            filterable: true,
        };
    }

    // Tour Number column
    static tourNumber(options?: { header?: string }): ColumnConfig {
        return {
            key: 'tourNumber',
            header: options?.header || 'SPEDITEUR.TABLE.HEADERS.TOUR-KEY',
            sortable: true,
            filterable: true,
        };
    }

    // External Tour Name column
    static externalTourName(options?: { header?: string }): ColumnConfig {
        return {
            key: 'externalTourName',
            header: options?.header || 'SPEDITEUR.TABLE.HEADERS.TOUR-NAME',
            sortable: true,
            filterable: true,
        };
    }

    // Original Consignor column
    static originalConsignor(): ColumnConfig {
        return {
            key: 'originalConsignor',
            header: 'GLOBAL.ORIGINAL_CONSIGNOR',
            sortable: true,
            filterable: true,
        };
    }

    // Original Consignor GLN column
    static originalConsignorGln(): ColumnConfig {
        return {
            key: 'originalConsignorGln',
            header: 'GLOBAL.GLN',
            sortable: true,
            filterable: true,
        };
    }

    // Delivery Note Bundle Number column
    static deliveryNoteBundleNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'deliveryNoteBundleNumber',
            header:
                options?.header ||
                'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.DELIVERY-NO',
            sortable: true,
            filterable: true,
        };
    }

    // delivery note status column
    static dnStatus(options?: { sortable?: boolean }): ColumnConfig {
        return {
            key: 'status',
            header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-STATUS',
            sortable: options?.sortable !== false,
            filterable: true,
            component: StatusBadgeComponent,
        };
    }

    // Delivery Note Bundle Status column
    static deliveryNoteBundleStatus(options?: {
        sortable?: boolean;
    }): ColumnConfig {
        return {
            key: 'deliveryNoteBundleStatus',
            header: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.BUNDLE-STATUS',
            sortable: options?.sortable !== false,
            filterable: true,
            component: StatusBadgeComponent,
        };
    }

    // Event Type column
    static eventType(options?: { header?: string }): ColumnConfig {
        return {
            key: 'eventType',
            header: options?.header || 'NOTIFICATION-VIEW.TABLE-HEADER.EVENT',
            sortable: true,
            filterable: true,
        };
    }

    // Organization Site Key column
    static organizationSiteKey(): ColumnConfig {
        return {
            key: 'organizationSiteKey',
            header: 'INCOMING-GOODS.TABLE.NOTIFICATIONS.LOCATION',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, value: ColumnValue) => ({
                value,
            }),
        };
    }

    // Bundle Nr column (for notifications)
    static bundleNr(): ColumnConfig {
        return {
            key: 'bundleNr',
            header: 'OUTGOING-GOODS.COLUMN-FILTERS.bundleKey',
            sortable: true,
            filterable: true,
        };
    }

    // Open Notes column (for notifications)
    static openNotes(): ColumnConfig {
        return {
            key: 'openNotes',
            header: '',
            sortable: false,
            filterable: false,
            width: '120px',
        };
    }

    // Carrier column (for tours)
    static carrier(): ColumnConfig {
        return {
            key: 'carrierName',
            header: 'INCOMING-GOODS.TABLE.CARRIER-NAME',
            sortable: true,
            filterable: true,
        };
    }

    // Notification Status column (extracts eventType from payload)
    static notificationStatus(options?: { header?: string }): ColumnConfig {
        return {
            key: 'notificationStatus',
            header:
                options?.header || 'INCOMING-GOODS.TABLE.NOTIFICATIONS.STATUS',
            sortable: true,
            filterable: true,
            component: TranslatedTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasEventPayload(row) ? row.payload.eventType : '',
            }),
        };
    }

    // Notification Bundle Number column (extracts bundleNumber from payload.deliveryNoteBundles[0])
    static notificationBundleNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationBundleNumber',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.BUNDLE_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value:
                    hasDeliveryNoteBundlesPayload(row) &&
                    row.payload.deliveryNoteBundles.length > 0
                        ? row.payload.deliveryNoteBundles
                              .map(
                                  (bundle: DeliveryNoteBundleEvents) =>
                                      bundle.bundleNumber
                              )
                              .join(', ')
                        : '',
            }),
        };
    }

    // Notification Driver Name column (extracts driverName from payload)
    static notificationDriverName(options?: { header?: string }): ColumnConfig {
        return {
            key: 'notificationDriverName',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.DRIVER_NAME',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDriverPayload(row)
                    ? row.payload.driverName || ''
                    : '',
            }),
        };
    }

    // Notification Driver Number Plate column (extracts driverNumberPlate from payload)
    static notificationDriverNumberPlate(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationDriverNumberPlate',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.LICENSE_PLATE_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDriverPayload(row)
                    ? row.payload.driverNumberPlate || ''
                    : '',
            }),
        };
    }

    // Notification Booking Number column (extracts BOOKING_NUMBER from selfCheckinData)
    static notificationBookingNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationBookingNumber',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.BOOKING_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDataPayload(row)
                    ? row.payload.selfCheckinData.BOOKING_NUMBER || ''
                    : '',
            }),
        };
    }

    // Notification Pager Number column (extracts PAGER_NUMBER from selfCheckinData)
    static notificationPagerNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationPagerNumber',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.PAGER_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDataPayload(row)
                    ? row.payload.selfCheckinData.PAGER_NUMBER || ''
                    : '',
            }),
        };
    }

    // Notification Phone Number column (extracts PHONE_NUMBER from selfCheckinData)
    static notificationPhoneNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationPhoneNumber',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.PHONE_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDataPayload(row)
                    ? row.payload.selfCheckinData.PHONE_NUMBER || ''
                    : '',
            }),
        };
    }

    // Notification Reference Number column (extracts REFERENCE_NUMBER from selfCheckinData)
    static notificationReferenceNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationReferenceNumber',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.REFERENCE_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDataPayload(row)
                    ? row.payload.selfCheckinData.REFERENCE_NUMBER || ''
                    : '',
            }),
        };
    }

    // Notification Time Slot Booking Number column (extracts TIME_SLOT_BOOKING_NUMBER from selfCheckinData)
    static notificationTimeSlotBookingNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationTimeSlotBookingNumber',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.TIME_SLOT_BOOKING_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDataPayload(row)
                    ? row.payload.selfCheckinData.TIME_SLOT_BOOKING_NUMBER || ''
                    : '',
            }),
        };
    }

    // Notification Waiting Number column (extracts WAITING_NUMBER from selfCheckinData)
    static notificationWaitingNumber(options?: {
        header?: string;
    }): ColumnConfig {
        return {
            key: 'notificationWaitingNumber',
            header:
                options?.header ||
                'INCOMING-GOODS.TABLE.NOTIFICATIONS.WAITING_NUMBER',
            sortable: true,
            filterable: true,
            component: CopyableTextDisplayComponent,
            componentInputs: (row: TableRowData, _value: ColumnValue) => ({
                value: hasSelfCheckinDataPayload(row)
                    ? row.payload.selfCheckinData.WAITING_NUMBER || ''
                    : '',
            }),
        };
    }

    // Discrepancies display column for showing rough/fine control discrepancies
    static discrepancies(options?: {
        header?: string;
        width?: string;
        bundleStatus?: string;
    }): ColumnConfig {
        return {
            key: 'discrepancies',
            header:
                options?.header ||
                'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DISCREPANCIES',
            sortable: true,
            filterable: true,
            width: options?.width,
            component: DiscrepanciesDisplayComponent,
            componentInputs: (
                row: TableRowData,
                value: ColumnValue,
                parentRow?: TableRowData
            ) => ({
                value,
                row,
                parentRow,
                bundleStatus:
                    options?.bundleStatus ||
                    (parentRow && hasStatus(parentRow)
                        ? parentRow.status
                        : undefined) ||
                    (hasStatus(row) ? row.status : undefined),
            }),
        };
    }

    // Pallet tickets display column for showing load carrier icons
    static palletTickets(options?: {
        header?: string;
        width?: string;
    }): ColumnConfig {
        return {
            key: 'palletTickets',
            header:
                options?.header ||
                'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.PALLET-TICKET',
            sortable: true,
            filterable: true,
            width: options?.width,
            component: PalletTicketsDisplayComponent,
            componentInputs: (
                row: TableRowData,
                value: ColumnValue,
                parentRow?: TableRowData
            ) => ({
                value,
                row,
                parentRow,
            }),
        };
    }

    // License plates display column for carrier number plates
    static carrierLicensePlate(options: {
        plateType: LicensePlateType;
        header?: string;
        width?: string;
    }): ColumnConfig {
        const defaultHeaders = {
            incoming: 'SPEDITEUR.TABLE.HEADERS.PLATE-OUT',
            outgoing: 'SPEDITEUR.TABLE.HEADERS.PLATE-IN',
        };

        return {
            key: `carrierNumberPlate${options.plateType === 'incoming' ? 'Incoming' : 'Outgoing'}`,
            header: options?.header || defaultHeaders[options.plateType],
            sortable: true,
            filterable: true,
            width: options?.width,
            component: LicensePlatesDisplayComponent,
            componentInputs: (
                row: TableRowData,
                value: ColumnValue,
                parentRow?: TableRowData
            ) => ({
                value,
                row,
                parentRow,
                plateType: options.plateType,
            }),
        };
    }

    // Generic column for any custom configuration
    static custom(config: ColumnConfig): ColumnConfig {
        return config;
    }
}
